import { DateTime } from 'luxon';
import { axios } from '@/plugins/axios';
import { $t } from "@/plugins/localization";
import merge from 'lodash/merge';
import Pager from '@/helpers/Pager';
import { Option, Pagination, Resource, Statement } from '@/helpers/Interfaces';
import { DepartmentEnum } from '@/modules/core/users/services/UsersService';
import { ChatRoomModel } from './ChatRoomService';
import ChatPager from '@/plugins/chatPager';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import { AuthModel } from '@/modules/core/auth/services/AuthService';

/**
 * ChatService
 */
export default class ChatService
{
    public static path = 'admin/chat';

    /**
     * @param data AddChatFormModel
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async createChat(data: AddChatFormModel): Promise<Resource<AddChatFormModel>>
    {
        return (await axios.post<Resource<AddChatFormModel>>(`${this.path}/`, data)).data;
    }

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`${this.path}/myRooms`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<ChatRoomModel>>
     */
    public static async fetchRoom(publicId: string): Promise<Resource<ChatRoomModel>>
    {
        return (await axios.get<Resource<ChatRoomModel>>(`${this.path}/${publicId}`)).data;
    }

    /**
     * @param message ChatMessageFormModel
     *
     * @returns Promise<Statement>
     */
    public static async sendMessage(message: ChatMessageFormModel): Promise<Statement>
    {
        return (await axios.post<Statement>(`${this.path}/message`,  message)).data;
    }

    /**
     * @param publicId string
     * @param pager ChatPager
     *
     * @returns Promise<Pagination<Resource<MessagePublicListModel>>>
     */
    public static async getMessages(publicId: string, pager: ChatPager): Promise<Pagination<MessagePublicListModel>>
    {
        return (await axios.get<Pagination<MessagePublicListModel>>(`${this.path}/${publicId}/message`, {
            params: pager.data()
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns boolean
     */
    public static async checkPermission(publicId: string): Promise<boolean>
    {
        return (await axios.get<boolean>(`${this.path}/room/${publicId}/user/permission`)).data;
    }

    public static async updateLastSeenMessage(model: LastSeenMessageModel) : Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/lastSeenMessage`, model)).data;
    }

    /**
      * @param publicId string
      *
      * @returns Promise<Statement>
      */
    public static async archive(publicId: string): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/archive/${publicId}`)).data as Statement;
    }

    /**
     * @param file File
     * @param container string
     * @param secured boolean
     *
     * @returns Promise<UploadResource>
     */
    public static async uploadPhoto(file: File, container: string, payload: any = null, secured: boolean = false): Promise<UploadResource>
    {
        const data = new FormData();

        data.append('container', container);
        data.append('scope', "public");
        data.append('payload', JSON.stringify(payload));
        data.append('secured', secured ? "true" : "false");
        data.append('file', file);

        const result = await axios.post<UploadResource>(`${this.path}/uploadPhotos`, data);

        return result.data;
    }

    /**
     * @param roomPublicId string
     * @param userPublicId string
     *
     * @returns Promise<Statement>
     */
    public static async removeUserFromChatRoom(roomPublicId: string, userPublicId: string): Promise<Resource<ChatRoomModel>>
    {
        return (await axios.delete<Resource<ChatRoomModel>>(`${this.path}/${roomPublicId}/user/${userPublicId}`)).data;
    }

    /**
     * @param roomPublicId string
     * @param userPublicId string
     *
     * @returns Promise<Statement>
     */
    public static async addChatRoomUser(roomPublicId: string, userPublicId: string): Promise<Resource<ChatRoomModel>>
    {
        return (await axios.put<Resource<ChatRoomModel>>(`${this.path}/${roomPublicId}/user/${userPublicId}`)).data;
    }

    public static async getUserRoles(): Promise<AuthModel>
    {
        return (await axios.get('/renters/auth/profile')).data;
    }

    public static async getMessagesByText(roomPublicId: string, search: string): Promise<Resource<MessagePublicListModel>>
    {
        return (await axios.get<Resource<MessagePublicListModel>>(`${this.path}/room/${roomPublicId}/search`, {
            params: {text: search}
        })).data;
    }

    /**
     * @param roomPublicId string
     *
     * @returns Promise<Statement>
     */
    public static async joinChat(roomPublicId: string): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${roomPublicId}/join`)).data;
    }
}

enum StatusEnum {
    Archival = 'Archival',
    Active = 'Active'
}

const StatusOptions = (): Option[] => ([
    { value: StatusEnum.Archival, text: $t('[[[Archiwalny]]]') },
    { value: StatusEnum.Active, text: $t('[[[Aktywny]]]') }
]);

const StatusName = (value: StatusEnum): string => StatusOptions().find(s => s.value === value)?.text || StatusEnum[value];

export {
    StatusEnum,
    StatusOptions,
    StatusName
};
export interface FilterModel
{
    name: string;
    propertyPublicId: string;
    status: Array<string> | string;
    cityPublicId: string;
    districtPublicId: string;
    messageFromUsersIds?: string;
}

export interface ListItemModel
{
    publicId: string;
    name: string;
    propertyName: string;
    status: string;
    districtPublicId: string;
    hasNewMessages: boolean;
    lastMessageDateUtc: DateTime;
    lastMessageFromUser: string;
}

export interface MessagePublicListModel
{
    id: number;
    publicId: string;
    date: DateTime;
    userId: number;
    userName: string;
    message: string;
    translatedMessage: string;
    isSystemMessage: boolean;
}

export interface ChatMessageFormModel
{
    roomPublicId: string;
    message: string;
    photos: FileResource[];
}

export interface ChatSettings
{
    dateFromUtc: Date;
    dateToUtc: Date;
    moderator: number;
}

export interface LastSeenMessageModel
{
    roomPublicId: string;
    lastSeenMessagePublicId: string
}

export interface MessageListByText
{
    roomPublicId: string;
    search: string;
    userId: number;
}

export interface AddChatFormModel
{
    id: number;
    publicId: string;
    name: string;
    message: string;
    selectedDepartments: DepartmentEnum[] | string;
    selectedUsersIds: number[];
    cityPublicId: string | null;
    districtPublicId: string | null;
    reservationPublicId: string;
    taskPublicId: string;
    routePublicId: string;
}
export interface ChatRoomsListItemModel{
    name: string;
    publicId: string;
    status: string;
    districtPublicId: string;
    hasNewMessages: boolean;
    propertyName: string;
}

interface UploadedResource
{
    upload?: File;
}

export interface UploadResource extends UploadedResource
{
    id: number;
    resourceType: string;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
    createdBy: string;
    modifiedBy: string;
    scope: string;
    folderId?: number;
    name: string;
    contentType: string;
    contentLength: number;
    url: string;
    isImage: boolean;
    isSecured: boolean;
    meta: Record<string, string>;
    content: Record<string, unknown>;
    media: string[];
}
